import type {
  TTemperatureRecommendation,
  TVisitStatus,
} from '@/entities/visit/model'

/**
 * Admission status of the donor card, derived from the deferrals in force.
 * Branch on this rather than on `label`, which is localized and gendered.
 */
export const EDonorStatusCode = {
  ADMITTED: 'ADMITTED',
  /** A temporary deferral is in force. */
  MEDICAL_DEFERRAL: 'MEDICAL_DEFERRAL',
  /**
   * A temporary deferral ended (and was not lifted), but no visit since has
   * passed the therapist's examination. Not admitted — becomes ADMITTED once a
   * visit started after the deferral is examined, or once the expired deferral
   * is lifted.
   */
  EXAMINATION_REQUIRED: 'EXAMINATION_REQUIRED',
  /** A permanent deferral is in force. */
  DEFERRAL: 'DEFERRAL',
  /** The deferrals could not be read — the donor is not treated as admitted. */
  UNKNOWN: 'UNKNOWN',
} as const

export type TDonorStatusCode =
  (typeof EDonorStatusCode)[keyof typeof EDonorStatusCode]

/** One business status of a visit (BS-285) — `label` is localized. */
export interface IActiveStep {
  code: TDonorStatusStep
  label: string
}

export interface IDonorStatus {
  label: string
  status_code: TDonorStatusCode
  /**
   * The furthest of `active_steps`, kept for compatibility. `null` when the
   * visit has no active stage left — it is finished.
   */
  step: TDonorStatusStep | null
  /**
   * Every business status the visit is in, furthest stage first — a reserve
   * donor can be in two at once (Забор + Донация). Empty when the visit is
   * finished.
   */
  active_steps?: IActiveStep[]
  visit_id: string
  is_admitted: boolean
}
export type IDonationType =
  | 'WHOLE_BLOOD'
  | 'PLASMAPHERESIS'
  | 'PLATELETPHERESIS'

export interface IDonorInfo {
  gender: 'MALE' | 'FEMALE'
  phone: string
  address: string
  age: number
  person_uuid: string
  full_name: string
  birth_date: string
  donor_category: string
  donation_type: IDonationType
  donation_type_name: string
  social_category: string
  photo_url: string
  blood_group_confirmed: boolean
}
export interface IGamification {
  donations_until_next: number
  progress_percent: number
  title: string
  donations_total: number
}

export interface ITheapistHistory {
  date: string
  conclusion: string
  status: TVisitStatus
  doctor_fio: string
}
export interface IVitals {
  height_cm: number
  weight_kg: number
  bmi: number
  blood_pressure: string
  pulse: number
  temperature_c: number
  temperature_recommendation: TTemperatureRecommendation
}
export interface IDonationHistory {
  date: string
  status: string
  donation_uuid: string
  /** e.g. `00-1330002626`; absent until production-core is deployed. */
  donation_number?: string
  donation_type: IDonationType
  volume_ml: number
}
export interface IDonorCardDetail {
  current_status: IDonorStatus
  donor_info: IDonorInfo
  gamification: IGamification
  therapist_history: ITheapistHistory[]
  latest_vitals?: IVitals
  donation_history: IDonationHistory[]
  /**
   * Female donors only: whether any certificate issued within the last year is
   * on file (in any organization). Absent for men, and absent for a woman when
   * the check itself failed — only an explicit `false` means "not admitted".
   */
  has_valid_certificate?: boolean
  /** Each deferral in force, once (BS-237). */
  active_deferrals?: IDonorActiveDeferral[]
}

export interface IDonorActiveDeferral {
  /** Journal id — pass as `deferral_id` to lift the deferral from the card. */
  deferral_id: string
  reason_code: string
  is_permanent: boolean
  deferral_until: string | null
  /** REGISTRY, QUESTIONNAIRE, DOCTOR, LAB. */
  source: string
  registry_name: string | null
  is_overridable: boolean
}

export interface IDonorDetail {
  profession: string
  visit_id: string
  gender: 'MALE' | 'FEMALE'
  donor_id: string
  last_name: string
  first_name: string
  middle_name: string
  full_name: string
  fio_initials: string
  birth_date: string
  /** Id of the uploaded photo file — resolve through `/v1/files/{id}/preview`. */
  photo_id: string | null
  blood_group: string
  rh_factor: string
  social_category_id: number
  passport_number: string
  passport_valid_from: string
  passport_valid_until: string
  passport_type: string
}

export interface IDonorCardHistory {
  id: string
  status: string
  donor_id: string
  visit_id: string
  card_number: string
  donor_category: string
  blood_group: string
  rh_factor: string
  is_admitted: boolean
  deferral_status: string
  deferral_reason: string
  change_reason: string
  created_at: string
  created_by: string
}

export interface IPrefilledDonor {
  birth_date: string
  first_name: string
  gender: 'MALE' | 'FEMALE'
  last_name: string
  middle_name: string | null
  passport_number: string
  passport_valid_from: string
  passport_valid_until: string
  /** Country code, e.g. `UZ` — matches the value of the country select. */
  country: string | null
  /** Region dictionary id, e.g. `2` — matches the value of the region select. */
  region_id: number | null
  region_name: string | null
}

export interface ICreateDonorResponse {
  donation_type_id: string
  donorId: string
  fioInitials: string
  status: string
  visitId: string
}

export const EDonationStatus = {
  SUCCESSFULL: 'SUCCESSFUL',
  UNSUCCESSFUL: 'UNSUCCESSFUL',
}

export type TDonationStatus =
  (typeof EDonationStatus)[keyof typeof EDonationStatus]

export const EDonorStatusStep = {
  REGISTRATION: 'REGISTRATION',
  EXAMINATION: 'EXAMINATION',
  SCREENING: 'SCREENING',
  /** Забор — the test tubes (BS-285). */
  BLOOD_COLLECTION: 'BLOOD_COLLECTION',
  SUITABLE: 'SUITABLE',
  LABORATORY: 'LABORATORY',
  UNDER_THERAPIST_REVIEW: 'UNDER_THERAPIST_REVIEW',
  DEFERRED: 'DEFERRED',
  DONATION: 'DONATION',
  PROCESSING: 'PROCESSING',
  EXPEDITION: 'EXPEDITION',
} as const

export type TDonorStatusStep =
  (typeof EDonorStatusStep)[keyof typeof EDonorStatusStep]
